import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout } from '../../../slices/authSlice';

const ICN = {
    x: (
        <svg viewBox="0 0 14 14" width="14" height="14" fill="none">
            <path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
    ),
    check: (
        <svg viewBox="0 0 14 14" width="13" height="13" fill="none">
            <path d="M3 7l3 3 5-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
};

const CONNECTORS = [
    { key: 'googledrive', name: 'Google Drive', cls: 'gd', letter: 'G', bg: '#ffffff', fg: '#1a73e8', cat: 'Storage', kind: 'folders', blurb: 'Sync source files from Drive folders.' },
    { key: 'dropbox', name: 'Dropbox', cls: 'db', letter: 'D', bg: '#0061FF', fg: '#ffffff', cat: 'Storage', kind: 'files', blurb: 'Pull files from any shared folder.' },
    { key: 'onedrive', name: 'OneDrive', cls: 'od', letter: 'O', bg: '#0078D4', fg: '#ffffff', cat: 'Storage', kind: 'folders', blurb: 'Microsoft 365 file sync.' },
    { key: 'slack', name: 'Slack', cls: 'sl', letter: 'S', bg: '#4A154B', fg: '#ffffff', cat: 'Communication', kind: 'channels', blurb: 'Notifications and slash commands.' },
    { key: 'figma', name: 'Figma', cls: 'fg', letter: 'F', bg: '#1a1a1a', fg: '#F24E1E', cat: 'Design', kind: 'files', blurb: 'Translate strings in design files.' },
    { key: 'github', name: 'GitHub', cls: 'gh', letter: 'G', bg: '#1a1a1a', fg: '#ffffff', cat: 'Code', kind: 'repos', blurb: 'String files committed to a repo.' },
    { key: 'gitlab', name: 'GitLab', cls: 'gl', letter: 'G', bg: '#ffffff', fg: '#FC6D26', cat: 'Code', kind: 'repos', blurb: 'Self-hosted & cloud repos.' },
    { key: 'notion', name: 'Notion', cls: 'no', letter: 'N', bg: '#ffffff', fg: '#1a1a1a', cat: 'Project mgmt', kind: 'pages', blurb: 'Pages, databases, and wikis.' },
    { key: 'contentful', name: 'Contentful', cls: 'cf', letter: 'C', bg: '#0681A4', fg: '#ffffff', cat: 'CMS', kind: 'spaces', blurb: 'Locale-aware content trees.' },
    { key: 'linear', name: 'Linear', cls: 'ln', letter: 'L', bg: '#5E6AD2', fg: '#ffffff', cat: 'Project mgmt', kind: 'teams', blurb: 'Translation tickets per language.' },
    { key: 'webflow', name: 'Webflow', cls: 'wf', letter: 'W', bg: '#146EF5', fg: '#ffffff', cat: 'CMS', kind: 'collections', blurb: 'Collections via the Data API.' },
    { key: 'sanity', name: 'Sanity', cls: 'sn', letter: 'S', bg: '#F03E2F', fg: '#ffffff', cat: 'CMS', kind: 'datasets', blurb: 'Studio plugin + GROQ queries.' },
];

const CATEGORIES = ['All', 'Storage', 'Code', 'Design', 'CMS', 'Communication', 'Project mgmt'];

const SCOPE_LABELS = {
    folders: ['Marketing / Q4', 'Brand / Acme HQ', 'Lifecycle / Growth', 'Legal / Compliance'],
    files: ['Mobile app v6.4', 'Marketing site', 'Brand library'],
    repos: ['acme/strings', 'acme/website', 'acme/mobile'],
    channels: ['#localization', '#marketing', '#eng-i18n'],
    pages: ['Product wiki', 'Help center', 'Release notes'],
    spaces: ['Marketing space', 'Docs space'],
    teams: ['Localization', 'Marketing'],
    collections: ['Blog posts', 'Landing pages', 'Product pages'],
    datasets: ['production', 'staging'],
};

const SourceTile = ({ c, size }) => (
    <span
        className={`src-tile ${size === 'lg' ? 'lg' : ''}`}
        style={{
            background: c.bg,
            color: c.fg,
            border: c.bg.toLowerCase() === '#ffffff' ? '1px solid var(--line)' : 'none',
        }}
    >
        {c.letter}
    </span>
);

const SourceDrawer = ({ connector, mode, onClose, onConnect, onDisconnect }) => {
    const c = connector;
    const locs = SCOPE_LABELS[c.kind] || ['Default location'];
    const [watched, setWatched] = useState(() => {
        const o = {};
        locs.forEach((l, i) => { o[l] = mode === 'configure' ? i < 2 : i === 0; });
        return o;
    });
    const [freq, setFreq] = useState('4h');
    const [twoWay, setTwoWay] = useState(true);
    const [pretranslate, setPretranslate] = useState(true);
    const connecting = mode === 'connect';

    return (
        <div className="src-drawer-wrap" onMouseDown={(e) => { if (e.target.classList.contains('src-drawer-wrap')) onClose(); }}>
            <div className="src-drawer">
                <div className="src-drawer-h">
                    <SourceTile c={c} size="lg" />
                    <div className="meta">
                        <b>{connecting ? `Connect ${c.name}` : c.name}</b>
                        <span>{c.cat} · {c.kind}</span>
                    </div>
                    <button type="button" className="src-drawer-x" onClick={onClose} aria-label="Close">{ICN.x}</button>
                </div>

                <div className="src-drawer-body">
                    <div className="set-block">
                        <div className="set-block-head">
                            <h3>Connection</h3>
                        </div>
                        {connecting ? (
                            <div className="src-auth">
                                <p>Authorize Rush Studio to read and write content in your {c.name} account. You'll be redirected to {c.name} to grant access.</p>
                                <button
                                    type="button"
                                    className="set-btn accent"
                                    style={{ alignSelf: 'flex-start' }}
                                    onClick={onConnect}
                                >
                                    Authorize with {c.name}
                                </button>
                            </div>
                        ) : (
                            <div className="set-list-row" style={{ padding: '14px 0' }}>
                                <div className="set-avatar"><SourceTile c={c} /></div>
                                <div className="li-main">
                                    <b>Connected</b>
                                    <span>Authorized via OAuth</span>
                                </div>
                                <div className="grow" />
                                <button type="button" className="set-btn sm">Reauthorize</button>
                            </div>
                        )}
                    </div>

                    <div className="set-block">
                        <div className="set-block-head flush">
                            <h3>What to sync</h3>
                            <p>Which {c.kind} Rush Studio watches for translatable content.</p>
                        </div>
                        {locs.map((l) => (
                            <div className="set-field" key={l}>
                                <div className="set-field-label"><div className="lbl">{l}</div></div>
                                <div className="set-field-control">
                                    <button
                                        type="button"
                                        onClick={() => setWatched((w) => ({ ...w, [l]: !w[l] }))}
                                        style={{
                                            width: 38, height: 22, borderRadius: 999, border: 0,
                                            background: watched[l] ? 'var(--accent)' : 'var(--bg-soft)',
                                            position: 'relative', cursor: 'pointer',
                                        }}
                                    >
                                        <span style={{
                                            position: 'absolute', top: 2,
                                            left: watched[l] ? 18 : 2,
                                            width: 18, height: 18, background: 'white', borderRadius: 999,
                                            transition: 'left 0.15s', boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                                        }} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="set-block">
                        <div className="set-block-head"><h3>Sync behavior</h3></div>
                        <div className="set-field">
                            <div className="set-field-label">
                                <div className="lbl">Sync frequency</div>
                                <div className="desc">How often we check for changes.</div>
                            </div>
                            <div className="set-field-control">
                                <select
                                    className="set-input"
                                    value={freq}
                                    onChange={(e) => setFreq(e.target.value)}
                                    style={{ minWidth: 140 }}
                                >
                                    <option value="realtime">Real-time</option>
                                    <option value="1h">Every hour</option>
                                    <option value="4h">Every 4 hours</option>
                                    <option value="daily">Daily</option>
                                </select>
                            </div>
                        </div>
                        <div className="set-field">
                            <div className="set-field-label">
                                <div className="lbl">Two-way sync</div>
                                <div className="desc">Push approved translations back to the source.</div>
                            </div>
                            <div className="set-field-control">
                                <button
                                    type="button"
                                    onClick={() => setTwoWay((v) => !v)}
                                    style={{
                                        width: 38, height: 22, borderRadius: 999, border: 0,
                                        background: twoWay ? 'var(--accent)' : 'var(--bg-soft)',
                                        position: 'relative', cursor: 'pointer',
                                    }}
                                >
                                    <span style={{
                                        position: 'absolute', top: 2,
                                        left: twoWay ? 18 : 2,
                                        width: 18, height: 18, background: 'white', borderRadius: 999,
                                        transition: 'left 0.15s', boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                                    }} />
                                </button>
                            </div>
                        </div>
                        <div className="set-field">
                            <div className="set-field-label">
                                <div className="lbl">Pre-translate on import</div>
                                <div className="desc">Run MT on new files automatically.</div>
                            </div>
                            <div className="set-field-control">
                                <button
                                    type="button"
                                    onClick={() => setPretranslate((v) => !v)}
                                    style={{
                                        width: 38, height: 22, borderRadius: 999, border: 0,
                                        background: pretranslate ? 'var(--accent)' : 'var(--bg-soft)',
                                        position: 'relative', cursor: 'pointer',
                                    }}
                                >
                                    <span style={{
                                        position: 'absolute', top: 2,
                                        left: pretranslate ? 18 : 2,
                                        width: 18, height: 18, background: 'white', borderRadius: 999,
                                        transition: 'left 0.15s', boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                                    }} />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="src-drawer-foot">
                    {!connecting && (
                        <button
                            type="button"
                            className="set-btn danger"
                            onClick={onDisconnect}
                        >
                            Disconnect
                        </button>
                    )}
                    <div className="grow" />
                    <button type="button" className="set-btn" onClick={onClose}>Cancel</button>
                    <button type="button" className="set-btn accent" onClick={onClose}>{ICN.check} Save changes</button>
                </div>
            </div>
        </div>
    );
};

const ConnectorCard = ({ c, connected, status, onClick }) => (
    <button
        type="button"
        className="src-card"
        onClick={onClick}
        style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--line)',
            borderRadius: 14,
            padding: 14,
            textAlign: 'left',
            cursor: 'pointer',
            color: 'inherit',
            fontFamily: 'inherit',
            display: 'flex',
            gap: 12,
            alignItems: 'flex-start',
        }}
    >
        <SourceTile c={c} size="lg" />
        <div className="src-card-body" style={{ flex: 1, minWidth: 0 }}>
            <b>{c.name}</b>
            <span>{c.cat} · {c.kind}</span>
            <div style={{ marginTop: 8, fontSize: 12, color: connected ? 'var(--green)' : 'var(--ink-3)', display: 'flex', alignItems: 'center', gap: 5 }}>
                {connected && <span style={{ width: 6, height: 6, borderRadius: 50, background: 'var(--green)' }} />}
                {status || c.blurb}
            </div>
        </div>
    </button>
);

const Sources = () => {
    const token = useSelector((s) => s.auth.token);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [integrations, setIntegrations] = useState([]);
    const [cat, setCat] = useState('All');
    const [drawer, setDrawer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!token) return;
        axios
            .get('/api/dashboard/integrations', { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => {
                setIntegrations(Array.isArray(r.data) ? r.data : []);
                setLoading(false);
            })
            .catch((err) => {
                if (err.response && err.response.status === 401) {
                    dispatch(logout());
                    navigate('/login');
                } else {
                    setError('Failed to load integrations.');
                    setLoading(false);
                }
            });
    }, [token, dispatch, navigate]);

    const connectedByKey = useMemo(() => {
        const map = {};
        integrations.forEach((i) => { map[i.key] = i; });
        return map;
    }, [integrations]);

    const filtered = useMemo(
        () => CONNECTORS.filter((c) => cat === 'All' || c.cat === cat),
        [cat],
    );
    const connected = filtered.filter((c) => connectedByKey[c.key] && connectedByKey[c.key].connected);
    const available = filtered.filter((c) => !connectedByKey[c.key] || !connectedByKey[c.key].connected);

    if (loading) return <div style={{ padding: 40, color: 'var(--ink-3)' }}>Loading sources…</div>;
    if (error) return <div style={{ padding: 40, color: 'crimson' }}>{error}</div>;

    return (
        <>
            <div className="rs-page-title-row">
                <h1 className="rs-page-title">Connected sources</h1>
                <span className="rs-page-title-count">{connected.length}</span>
            </div>
            <p className="rs-page-sub">Pull source content from external tools — and push translations back to where they live.</p>

            <div className="src-stats">
                <div className="src-stat">
                    <div className="v">{connected.length}<span className="u">connected</span></div>
                    <div className="l">Active sources</div>
                </div>
                <div className="src-stat">
                    <div className="v">{CONNECTORS.length}<span className="u">total</span></div>
                    <div className="l">Available connectors</div>
                </div>
                <div className="src-stat">
                    <div className="v">{new Set(connected.map((c) => c.cat)).size}</div>
                    <div className="l">Categories in use</div>
                </div>
                <div className="src-stat">
                    <div className="v">—</div>
                    <div className="l">Last sync</div>
                </div>
            </div>

            <div className="rs-views">
                {CATEGORIES.map((c) => (
                    <button
                        key={c}
                        type="button"
                        className={`rs-view-tab ${cat === c ? 'on' : ''}`}
                        onClick={() => setCat(c)}
                    >
                        {c}
                    </button>
                ))}
            </div>

            <div className="rs-card" style={{ marginBottom: 16 }}>
                <div className="rs-card-h">
                    <h3>Connected</h3>
                    <span className="count">{connected.length}</span>
                </div>
                {connected.length === 0 ? (
                    <div style={{ color: 'var(--ink-3)', padding: '12px 0' }}>
                        Nothing connected yet. Pick one below to get started.
                    </div>
                ) : (
                    <div className="src-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
                        {connected.map((c) => {
                            const info = connectedByKey[c.key];
                            return (
                                <ConnectorCard
                                    key={c.key}
                                    c={c}
                                    connected
                                    status={info.status}
                                    onClick={() => setDrawer({ connector: c, mode: 'configure' })}
                                />
                            );
                        })}
                    </div>
                )}
            </div>

            <div className="rs-card">
                <div className="rs-card-h">
                    <h3>Available</h3>
                    <span className="count">{available.length}</span>
                </div>
                <div className="src-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
                    {available.map((c) => (
                        <ConnectorCard
                            key={c.key}
                            c={c}
                            connected={false}
                            status={c.blurb}
                            onClick={() => setDrawer({ connector: c, mode: 'connect' })}
                        />
                    ))}
                </div>
            </div>

            {drawer && (
                <SourceDrawer
                    connector={drawer.connector}
                    mode={drawer.mode}
                    onClose={() => setDrawer(null)}
                    onConnect={() => setDrawer(null)}
                    onDisconnect={() => setDrawer(null)}
                />
            )}
        </>
    );
};

export default Sources;
